import Component from '@glimmer/component'
import { service } from '@ember/service'
import { isPresent } from '@ember/utils'
import { colors } from '@colors'
import { reportingPlotLine as plotLine } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { t, type IntlService } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { uniq } from 'lodash'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesBar } from 'teacher-ui-client/components/giraffe/primitives/series-bar'
import {
  SeriesColumn,
  type ChartSpacing,
  type SeriesColumnOptions,
} from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { array } from '@ember/helper'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type Highcharts from 'highcharts'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'

type MapData = {
  map: number
  average_score: number
  student_count: number
  attempts: number
}

export interface AssessmentScoresAveragesData {
  average_score: number
  scores: [
    { range: '0-49'; student_count: number },
    { range: '50-79'; student_count: number },
    { range: '80-100'; student_count: number },
  ]
  maps: MapData[]
  grade_position: number
}

interface Args {
  data: AssessmentScoresAveragesData
  grade: string
  precinct: typeof Precinct.RE_READING | typeof Precinct.REX_READING | typeof Precinct.MS_LESSONS
  title: string
  tooltip: string
}

interface Signature {
  Args: Args
  Element: HTMLDivElement
}

export class AssessmentScoresAverages extends Component<Signature> {
  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  @service lessonInfo!: LessonInfoService

  chartSpacing: ChartSpacing = [10, 10, 10, 10]

  summaryTitle = 'Average Score'

  get chartTitle() {
    const text = this.args.title
    const enabled = true
    return {
      text,
      enabled,
      y: 10,
    }
  }

  get averageScore() {
    return this.args.data.average_score
  }

  get scores() {
    return [...this.args.data.scores].sort((a, b) => parseInt(b.range.split('-')[0]) - parseInt(a.range.split('-')[0]))
  }

  get studentCounts() {
    return this.scores.map((score) => score.student_count)
  }

  get maxStudentCount() {
    return Math.max(...this.studentCounts)
  }

  get summaryChartXAxisTitle(): Highcharts.AxisTitleOptions {
    return {
      text: 'Scores %',
      align: 'high',
      rotation: 0,
      offset: 0,
    }
  }

  /**
   * Returns the colours needed for fail colour, pass colour and success colour
   */
  get chartColors(): { successColor: string; passColor: string; failColor: string } {
    switch (this.args.precinct) {
      case Precinct.MS_LESSONS:
      case Precinct.RE_READING:
        return {
          successColor: colors.msGreen300,
          passColor: colors.juicyOrange300,
          failColor: colors.watermelonyRed300,
        }
      case Precinct.REX_READING:
        return {
          successColor: colors.grapeyGreen300,
          passColor: colors.juicyOrange300,
          failColor: colors.watermelonyRed300,
        }
      default:
        return {
          successColor: colors.oceanyBlue300,
          passColor: colors.oceanyBlue300,
          failColor: colors.oceanyBlue300,
        }
    }
  }

  /**
   * Returns fail colour, pass colour and success colour for seriesBar
   */
  get chartColorsArray() {
    return [this.chartColors.successColor, this.chartColors.passColor, this.chartColors.failColor]
  }

  get zones(): Highcharts.SeriesZonesOptionsObject[] {
    return [
      { value: 50, color: this.chartColors.failColor },
      { value: 80, color: this.chartColors.passColor },
      { color: this.chartColors.successColor },
    ]
  }

  yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject) {
    return `${this.value}%`
  }

  get toolTipFormatter() {
    const { intl } = this

    return function (this: Highcharts.Point) {
      const { map } = this as Highcharts.Point & { map: MapData }
      const score = map.average_score
      const studentCount = map.student_count
      const { attempts } = map

      const studentCountLabel = intl.t('reporting.statBoxes.assessmentScoresTotals.studentCount')

      return `Attempts: ${attempts}<br>Score: ${score}%<br>${studentCountLabel}: ${studentCount}`
    }
  }

  get dataMaps(): MapData[] {
    return [...(this.args.data?.maps ?? [])].sort((a, b) => a.map - b.map)
  }

  get mapNumbersFromData(): number[] {
    return this.dataMaps.map((map) => map.map)
  }

  get lessons() {
    return this.lessonInfo.lessonsFor(this.args.precinct)
  }

  /**
   * Unique grades for the maps in the map data. Grades cover multiple maps, so this covers a wider range of lessons
   */
  get gradesForMaps(): number[] {
    const lessonsForMaps = this.lessons.filter((l) => l.map && this.mapNumbersFromData.includes(l.map))
    return uniq(lessonsForMaps.map((l) => l.grade_position).filter(isPresent))
  }

  /**
   * Fill in map data from the lowest map _of the grade_ in the report data, to the highest map in the report data.
   */
  get filledDataMaps(): MapData[] {
    // determine which maps we're covering
    const highestDataMapNumber = Math.max(...this.mapNumbersFromData)
    const mapsForGrades = uniq(
      this.lessons
        .filter((l) => typeof l.grade_position === 'number' && this.gradesForMaps.includes(l.grade_position))
        .map((l) => l.map)
        .filter(isPresent)
        // dont go past map where student is up to
        .filter((map) => map <= highestDataMapNumber),
    )

    // for those maps, fill in defaults for maps without score data
    const defaultMapData = { attempts: 0, average_score: 0, student_count: 0 }
    return mapsForGrades.map(
      (mapNumber) => this.dataMaps.find((map) => map.map === mapNumber) ?? { map: mapNumber, ...defaultMapData },
    )
  }

  /**
   * Return a plotline for each grade that is covered by the charts' maps
   */
  get plotLines(): Highcharts.XAxisPlotLinesOptions[] {
    return this.gradesForMaps
      .map((g) => {
        /* Find first lesson for grade, grab its map number, find the index of the chart data that represents that map,
         * this is the axis position of where the plotline will be drawn. The filledDataMaps is used for the chart data */
        const firstMapForGrade = this.lessons.find((l) => l.grade_position === g)?.map
        const axisPosition = this.filledDataMaps.findIndex((dataMap) => dataMap.map === firstMapForGrade)
        if (axisPosition === -1) return null

        const label = this.gradeSets.fullNameFromGradeSetByPosition(g) ?? ''

        // this places plot line on tick mark
        return plotLine(axisPosition - 0.5, label, {
          align: 'left',
          icon: '▶',
          x: -2,
          y: -7,
        })
      })
      .filter(isPresent)
  }

  get summaryChartData(): Highcharts.SeriesBarOptions[] {
    const { scores } = this
    const data = scores.map((score, index) => {
      return { x: index, y: score.student_count, score }
    })
    return [{ name: 'student_counts', data, type: 'bar' }]
  }

  get chartData(): SeriesColumnOptions[] {
    const { filledDataMaps, zones } = this
    const data = filledDataMaps.map((map, index) => {
      return { x: index, y: map.average_score, map }
    })
    return [{ name: 'average_scores', data, zones, type: 'column' }]
  }

  get summaryChartCategories() {
    return this.scores.map((score) => score.range.toString())
  }

  get chartCategories() {
    return this.filledDataMaps.map((mapItem) => mapItem.map.toString())
  }

  <template>
    <div data-test-assessment-scores-averages class="relative h-full" ...attributes>
      <ChartInfoTooltip @tooltipContent={{@tooltip}} />
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title="{{this.summaryTitle}}: {{this.averageScore}}%"
            @grade={{@grade}}
            @colorScheme={{SummaryBoxColorScheme.Orange}}
          >
            <SeriesBar
              @data={{this.summaryChartData}}
              @categories={{this.summaryChartCategories}}
              @colors={{this.chartColorsArray}}
              @colorByPoint={{true}}
              @plotBackgroundColor="transparent"
              @xAxisLabel={{this.summaryChartXAxisTitle}}
              @yRange={{array 0 this.maxStudentCount}}
              @yAxisLabel={{t "reporting.statBoxes.assessmentScoresTotals.numOfStudents"}}
            />
          </SummaryBox>
        </:left>
        <:right>
          <SeriesColumn
            @data={{this.chartData}}
            @categories={{this.chartCategories}}
            @colorByPoint={{true}}
            @title={{this.chartTitle}}
            @toolTipFormatter={{this.toolTipFormatter}}
            @yAxisLabelFormatter={{this.yAxisLabelFormatter}}
            @xAxisPlotLines={{this.plotLines}}
            @chartSpacing={{this.chartSpacing}}
            @xAxisLabel="Map"
            @yRange={{array 0 100}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default AssessmentScoresAverages
